import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";

import * as actions from "../../store/actions/index";
import { classNames, withRouter } from "../../shared/utility";
import { CampaignDetailsViewModel } from "../../viewmodels/campaigns/CampaignDetailsViewModel";
import CampaignDetailsForm from "../../components/forms/campaigns/CampaignDetailsForm";
import LoadingSvg from "../../components/spinners/LoadingSvg";
import EmptyModal from "../../components/modals/EmptyModal";
import CampaignAnalytics from "./CampaignAnalytics";

function CampaignDetails(props) {
  const { logout, addSnackbar } = props;
  const { campaignId } = useParams();
  const [currentTab, setCurrentTab] = useState(0);

  const {
    campaign,
    campaignAnalytics,
    campaignDistributedAnalytics,
    emptyModal,
    generateCampaignReport,
    getCampaign,
    loading,
    reportInProgress,
    setEmptyModelOpen,
    updateCampaign,
  } = CampaignDetailsViewModel(logout, campaignId, addSnackbar);

  useEffect(() => {
    getCampaign(campaignId);
    campaignDistributedAnalytics(campaignId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  const tabs = [
    { name: "Campaign Details", href: "#" },
    { name: "Campaign Analytics", href: "#" },
  ];

  const handleUpdateCampaign = useCallback(
    (c, values) => updateCampaign(c.id, values),
    [updateCampaign],
  );

  const renderComponent = (index) => {
    switch (index) {
      case 0:
        return (
          <div key={0} className="-mt-2 bg-white">
            {loading ? (
              <div className="flex items-center gap-2 p-4">
                <LoadingSvg />
                <span className="text-gray-600">Loading</span>
              </div>
            ) : (
              <CampaignDetailsForm
                campaign={campaign}
                actionCall={handleUpdateCampaign}
                loading={loading}
              />
            )}
          </div>
        );
      case 1:
        return (
          <div key={1} className="-mt-2 bg-white">
            <CampaignAnalytics
              analyticsData={campaignAnalytics}
              campaignId={campaignId}
              generateCampaignReport={generateCampaignReport}
              reportInProgress={reportInProgress}
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50">
      <header className="bg-white shadow sticky top-0 z-50 hide-for-print">
        <div className="max-w-7xl mx-auto py-4 px-4 flex items-center justify-between flex-wrap sm:flex-nowrap">
          <div className="flex px-4 sm:px-12">
            <h1 className="text-3xl font-bold text-gray-900">
              {loading ? "Loading..." : (campaign?.name ?? "Campaign")}
            </h1>
          </div>
        </div>
      </header>
      <main>
        <div className="p-6">
          <div className="hide-for-print">
            <div className="hidden sm:block">
              <nav
                className="relative z-0 rounded-lg shadow flex divide-x divide-gray-200"
                aria-label="Tabs"
              >
                {tabs.map((tab, index) => (
                  <a
                    key={tab.name}
                    href={tab.href}
                    className={classNames(
                      index === currentTab
                        ? "text-gray-900"
                        : "text-gray-500 hover:text-gray-700",
                      index === 0 ? "rounded-l-lg" : "",
                      index === tabs.length - 1 ? "rounded-r-lg" : "",
                      "group relative min-w-0 flex-1 overflow-hidden bg-white py-4 px-4 text-sm font-medium text-center hover:bg-gray-50 focus:z-10",
                    )}
                    aria-current={index === currentTab ? "page" : undefined}
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentTab(index);
                    }}
                  >
                    <span>{tab.name}</span>
                    <span
                      aria-hidden="true"
                      className={classNames(
                        index === currentTab
                          ? "bg-indigo-500"
                          : "bg-transparent",
                        "absolute inset-x-0 bottom-0 h-0.5",
                      )}
                    />
                  </a>
                ))}
              </nav>
            </div>
          </div>

          <div className="px-4 py-6 sm:px-0">
            <div className="sm:col-span-9 py-5 border-b border-gray-200">
              {currentTab === 0 && renderComponent(0)}
              {currentTab === 1 && renderComponent(1)}
            </div>
          </div>
        </div>
      </main>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
  logout: () => dispatch(actions.logout()),
  addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CampaignDetails),
);
